import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, Trophy, Crown, Gift, ArrowLeft } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { MODULE_MASTERED_THRESHOLD } from '../../config/constants';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered, useDjayCustomization } from '../../mascot';
import { STANDALONE_COSMETIC_GATES } from '../../mascot/useDjayCustomization';
import type { DjayVariant, MascotRarity } from '../../mascot';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { ACHIEVEMENTS, AchievementCheckData, CATEGORY_LABELS, Achievement } from '../progress/AchievementsSection';
import { DjayShowcase } from './DjayShowcase';
import { AchievementCategoryGroup } from './AchievementCategoryGroup';
interface CollectionPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onNavigate?: (view: string) => void;
}
const RARITY_STYLES: Record<MascotRarity, {
  border: string;
  bg: string;
  text: string;
  glow: string;
  label: string;
}> = {
  common: {
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    glow: '',
    label: 'Gewoon'
  },
  uncommon: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]',
    label: 'Ongewoon'
  },
  rare: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    label: 'Zeldzaam'
  },
  legendary: {
    border: 'border-purple-400',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    glow: 'shadow-[0_0_20px_rgba(167,139,250,0.5)]',
    label: 'Legendarisch'
  },
  secret: {
    border: 'border-rose-400',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    glow: 'shadow-[0_0_20px_rgba(244,63,94,0.5)]',
    label: 'Geheim'
  }
};
export function CollectionPage({
  state,
  selectedGroup,
  onNavigate
}: CollectionPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    markCosmeticUnlocked
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant,
    selectedAccessory,
    setSelectedAccessory,
    selectedTier,
    setSelectedTier
  } = useDjaySelection();
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  const [focusedVariant, setFocusedVariant] = useState<string | null>(null);
  useEffect(() => {
    const focusId = sessionStorage.getItem('collectionFocus');
    if (focusId) {
      sessionStorage.removeItem('collectionFocus');
      setFocusedVariant(focusId);
      setTimeout(() => {
        document.getElementById(`variant-${focusId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      setTimeout(() => setFocusedVariant(null), 3000);
    }
  }, []);
  // --- Mascot Variants (single source of truth) ---
  const {
    variants: mascotVariants,
    unlockedCount: unlockedVariantsCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // --- Achievements Logic ---
  const totalQuestionsAnswered = useMemo(() => {
    return MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0);
  }, [getAnsweredQuestions]);
  const masteredCount = useMemo(() => {
    let mastered = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      if (mastery >= MODULE_MASTERED_THRESHOLD) mastered++;
    });
    return mastered;
  }, [getModuleMasteryForGroup]);
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const checkData: AchievementCheckData = useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions]);
  const unlockedCosmetics = useMemo(() => state.stats.unlockedCosmetics ?? [], [state.stats.unlockedCosmetics]);
  const {
    accessories,
    tiers,
    unlockedAccessoriesCount,
    unlockedTiersCount
  } = useDjayCustomization(checkData, unlockedCosmetics);
  // Persist a standalone cosmetic the moment its live stat gate is first met,
  // so it stays "owned" even if the underlying stat later dips (e.g. weekly
  // counters resetting). Idempotent: markCosmeticUnlocked no-ops on duplicates.
  useEffect(() => {
    STANDALONE_COSMETIC_GATES.forEach(gate => {
      if (gate.isMet(checkData) && !unlockedCosmetics.includes(gate.id)) {
        markCosmeticUnlocked(gate.id);
      }
    });
  }, [checkData, unlockedCosmetics, markCosmeticUnlocked]);
  const achievementsWithProgress = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  const unlockedAchievementsCount = achievementsWithProgress.filter(a => a.isUnlocked).length;
  const totalAchievementsCount = achievementsWithProgress.length;
  // Combined Progress — de-duplicated union of all distinct collectibles.
  // Most variants are GRANTED BY an achievement (achievement.djayVariantUnlocked),
  // so counting both tracks naively would double-count the same reward. We count
  // every achievement once, plus only the "standalone" variants that no
  // achievement owns (e.g. the always-on default + the secret 'goud' easter-egg).
  // The two per-track StatTiles below still show full per-track tallies.
  const achievementOwnedVariants = useMemo(() => new Set(ACHIEVEMENTS.map(a => a.djayVariantUnlocked).filter((v): v is DjayVariant => !!v)), []);
  const standaloneVariants = useMemo(() => mascotVariants.filter(v => !achievementOwnedVariants.has(v.id)), [mascotVariants, achievementOwnedVariants]);
  const totalCollectibles = totalAchievementsCount + standaloneVariants.length;
  const totalUnlocked = unlockedAchievementsCount + standaloneVariants.filter(v => v.unlocked).length;
  const progressPercentage = totalCollectibles > 0 ? Math.round(totalUnlocked / totalCollectibles * 100) : 0;
  // Mirror of the Voortgang GhostTrophy: surface the single closest-to-done
  // locked collectible so the learner always has a concrete "next reward" to
  // chase from within the Collection too. Variant-backed rewards deep-link
  // (scroll + pulse) to their thumbnail via the same focus mechanism.
  const nextReward = useMemo(() => {
    type Candidate = {
      name: string;
      pct: number;
      current: number;
      max: number;
      label: string;
      variantId: DjayVariant | null;
    };
    const candidates: Candidate[] = [];
    // Locked standalone variants (default/secret etc. not owned by an achievement)
    standaloneVariants.forEach(v => {
      if (!v.unlocked && v.progress.max > 0) {
        candidates.push({
          name: v.name,
          pct: v.progress.current / v.progress.max,
          current: v.progress.current,
          max: v.progress.max,
          label: v.progress.label,
          variantId: v.id
        });
      }
    });
    // Locked achievements (the primary collectible track)
    achievementsWithProgress.forEach(a => {
      if (!a.isUnlocked && a.progress && a.progress.max > 0) {
        candidates.push({
          name: a.name,
          pct: a.progress.current / a.progress.max,
          current: a.progress.current,
          max: a.progress.max,
          label: a.progress.label,
          variantId: a.djayVariantUnlocked ?? null
        });
      }
    });
    // Closest to completion (but not yet done) wins.
    const sorted = candidates.filter(c => c.pct < 1).sort((a, b) => b.pct - a.pct);
    return sorted[0] ?? null;
  }, [standaloneVariants, achievementsWithProgress]);
  const focusVariant = (variantId: string) => {
    setFocusedVariant(variantId);
    setTimeout(() => {
      document.getElementById(`variant-${variantId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 50);
    setTimeout(() => setFocusedVariant(null), 3000);
  };
  return <PageContainer className="pb-20" data-id="element-1144">
      <motion.div variants={itemVariants} className="mb-4" data-id="element-1145">
        {onNavigate && <button onClick={() => onNavigate('progress')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold text-sm transition-colors" data-id="element-1146">
            <ArrowLeft className="w-4 h-4" data-id="element-1147" />
            Terug naar Voortgang
          </button>}
      </motion.div>
      <motion.div variants={itemVariants} data-id="element-1148">
        <PageHero title="Mijn Collectie" subtitle="Verdien prestaties door te oefenen en speel unieke Djay-looks vrij!" gradientClasses="from-amber-500 via-orange-500 to-rose-500" djayEmotion="excited" djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} icon={Sparkles} iconBgClass="bg-amber-400" floatingEmojis={['✨', '🏆', '⭐']} data-id="element-1149" />
      </motion.div>

      <motion.div variants={itemVariants} className="mt-8 space-y-8" data-id="element-1150">
        {/* Overall Progress Card */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl border-2 border-slate-100 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-1151">
          <div className="flex items-center gap-4" data-id="element-1152">
            <div className="relative w-16 h-16 flex-shrink-0" data-id="element-1153">
              <svg className="w-16 h-16 transform -rotate-90" data-id="element-1154">
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-1155" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F59E0B" strokeWidth="6" strokeDasharray="175.9" strokeDashoffset={175.9 - progressPercentage / 100 * 175.9} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1156" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1157">
                <Crown className="w-6 h-6 text-amber-500" data-id="element-1158" />
              </div>
            </div>
            <div data-id="element-1159">
              <h2 className="text-xl font-black text-slate-800" data-id="element-1160">
                Verzameling compleet
              </h2>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1161">
                {totalUnlocked} van de {totalCollectibles} behaald ·{' '}
                {progressPercentage}%
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 w-full sm:w-auto" data-id="element-1162">
            <StatTile icon={Trophy} iconColorClass="text-amber-500" label="Prestaties" value={`${unlockedAchievementsCount}/${totalAchievementsCount}`} progress={unlockedAchievementsCount / totalAchievementsCount * 100} progressColorClass="bg-amber-500" data-id="element-1163" />
            <StatTile icon={Star} iconColorClass="text-orange-500" label="Varianten" value={`${unlockedVariantsCount}/${TOTAL_VARIANTS}`} progress={unlockedVariantsCount / TOTAL_VARIANTS * 100} progressColorClass="bg-orange-500" data-id="element-1164" />
          </div>
        </div>

        {/* Next reward teaser — mirrors the Voortgang GhostTrophy so the
            growth ↔ collection loop reads the same in both directions. */}
        {nextReward && <motion.button type="button" onClick={() => nextReward.variantId && focusVariant(nextReward.variantId)} whileHover={nextReward.variantId ? {
        scale: 1.01
      } : undefined} whileTap={nextReward.variantId ? {
        scale: 0.99
      } : undefined} className={`w-full flex items-center gap-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl text-left transition-shadow ${nextReward.variantId ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`} aria-label={nextReward.variantId ? `Volgende beloning: ${nextReward.name}. Bekijk in je collectie.` : `Volgende beloning: ${nextReward.name}.`} data-id="element-1165">
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-amber-100 text-amber-500 flex items-center justify-center" data-id="element-1166">
              <Gift className="w-6 h-6" data-id="element-1167" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1168">
              <p className="text-[10px] sm:text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1" data-id="element-1169">
                <Sparkles className="w-3 h-3" data-id="element-1170" /> Volgende beloning
              </p>
              <h3 className="text-sm sm:text-base font-black text-slate-800 leading-tight truncate" data-id="element-1171">
                {nextReward.name}
              </h3>
              <div className="mt-1.5 flex items-center gap-2" data-id="element-1172">
                <div className="h-2 flex-1 bg-amber-100 rounded-full overflow-hidden" data-id="element-1173">
                  <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                width: 0
              }} animate={{
                width: `${Math.round(nextReward.pct * 100)}%`
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} data-id="element-1174" />
                </div>
                <span className="text-[11px] font-bold text-slate-500 flex-shrink-0" data-id="element-1175">
                  {nextReward.current}/{nextReward.max} {nextReward.label}
                </span>
              </div>
            </div>
          </motion.button>}

        {/* Interactive Djay Showcase */}
        <DjayShowcase variants={mascotVariants} selectedVariant={selectedVariant} onSelectVariant={setSelectedVariant} accessories={accessories} selectedAccessory={selectedAccessory} onSelectAccessory={setSelectedAccessory} tiers={tiers} selectedTier={selectedTier} onSelectTier={setSelectedTier} focusedVariant={focusedVariant} rarityStyles={RARITY_STYLES} data-id="element-1176" />

        {/* Prestaties Section */}
        <div className="space-y-8 pt-4" data-id="element-1177">
          <SectionHeader title="Prestaties" icon={Trophy} accentColor="bg-amber-100 text-amber-600" data-id="element-1178" />

          <div className="space-y-12" data-id="element-1179">
            {(Object.keys(CATEGORY_LABELS) as Achievement['category'][]).map(category => {
            const categoryAchievements = achievementsWithProgress.filter(a => a.category === category);
            if (categoryAchievements.length === 0) return null;
            return <AchievementCategoryGroup key={category} title={CATEGORY_LABELS[category]} achievements={categoryAchievements} mascotVariants={mascotVariants} data-id="element-1180" />;
          })}
          </div>
        </div>
      </motion.div>
    </PageContainer>;
}